// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Send, Cpu, Shield, Clock, AlertTriangle, Play, Sparkles, Check, CheckCircle2, DollarSign, Mail, Users, Building, Landmark
} from 'lucide-react';

interface OutreachTarget {
  id: 'CORPORATE' | 'GOVERNMENT' | 'INDIVIDUAL';
  title: string;
  icon: React.ReactNode;
  defaultPayload: string;
  depositAmount: number;
}

export const NexoraXHermesNode: React.FC = () => {
  const [activeTarget, setActiveTarget] = useState<'CORPORATE' | 'GOVERNMENT' | 'INDIVIDUAL'>('CORPORATE');
  const [customPayload, setCustomPayload] = useState<string>('');
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [trialTimeLeft, setTrialTimeLeft] = useState<number>(60);
  const [outreachStatus, setOutreachStatus] = useState<'idle' | 'transmitting' | 'success'>('idle');
  const [progress, setProgress] = useState<number>(0);

  const targets: Record<'CORPORATE' | 'GOVERNMENT' | 'INDIVIDUAL', OutreachTarget> = {
    CORPORATE: {
      id: 'CORPORATE',
      title: 'Corporate Outposts',
      icon: <Building className="w-4 h-4 text-amber-500" />,
      defaultPayload: "Your operational latency is currently costing you £X,XXX/hr. NexoraX Hermes nodes are pre-configured to synchronize your workflow and force-close 40% more revenue within the first 72 hours. Deployment is immediate.",
      depositAmount: 1500
    },
    GOVERNMENT: {
      id: 'GOVERNMENT',
      title: 'Government Entities',
      icon: <Landmark className="w-4 h-4 text-amber-500" />,
      defaultPayload: "Institutional administrative queues are failing to meet real-time policy demands. Our NexoraX Infrastructure nodes provide secure, zero-latency governance pathways. Immediate node-locking is available.",
      depositAmount: 2500
    },
    INDIVIDUAL: {
      id: 'INDIVIDUAL',
      title: 'High-Ticket Individuals',
      icon: <Users className="w-4 h-4 text-amber-500" />,
      defaultPayload: "Access the NexoraX Neural Matrix. Outperform your market competitors using pre-configured AI Agent Swarms designed for high-density revenue generation.",
      depositAmount: 750
    }
  };

  // Sync custom payload when active target shifts
  useEffect(() => {
    setCustomPayload(targets[activeTarget].defaultPayload);
    setOutreachStatus('idle');
    setProgress(0);
  }, [activeTarget]);

  // 60S Temporal countdown protocol
  useEffect(() => {
    if (trialTimeLeft > 0 && !isLocked) {
      const timer = setTimeout(() => setTrialTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (trialTimeLeft === 0) {
      setIsLocked(true);
    }
  }, [trialTimeLeft, isLocked]);

  const handleResetLock = () => {
    setTrialTimeLeft(60);
    setIsLocked(false);
    setOutreachStatus('idle');
    setProgress(0);
  };

  const dispatchHermesOutreach = () => {
    if (isLocked) return;
    setOutreachStatus('transmitting');
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setOutreachStatus('success');
          return 100;
        }
        return prev + 10;
      });
    }, 150);
  };

  // Financial Split Matrix computations
  const depositVal = targets[activeTarget].depositAmount;
  const levyVal = depositVal * 0.01;
  const splitVal = levyVal / 3;
  const afterLevyVal = depositVal - levyVal;
  const gavelFeeVal = afterLevyVal * 0.20;
  const netSettleVal = afterLevyVal - gavelFeeVal;

  return (
    <div id="NexoraX-hermes-root" className="w-full max-w-7xl mx-auto p-4 md:p-8 space-y-6 bg-black text-slate-100 rounded-3xl border border-amber-500/30 shadow-[0_0_50px_rgba(217,119,6,0.06)] relative overflow-hidden">
      
      {/* Background glow effects */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-red-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-neutral-900 pb-6 relative z-10">
        <div>
          <div className="flex items-center gap-2 text-amber-500 font-mono text-xs font-bold uppercase tracking-widest">
            <Send className="w-4 h-4 animate-pulse" />
            <span>Node-12 Nex Protocol Outpost</span>
          </div>
          <h2 className="text-xl md:text-2xl font-sans font-black text-white uppercase tracking-wider mt-1.5">
            Nex Acquisition & Outreach Engine <span className="text-amber-500 font-mono">(Multi-Vector Outreach)</span>
          </h2>
          <p className="text-[10px] text-slate-500 font-mono tracking-wider mt-1 uppercase">
            Authorized Acquisition System: Director Lead Director // Aggressive Outreach Channel Locking
          </p>
        </div>

        {/* Temporal protection system status */}
        <div className="flex items-center gap-3 shrink-0">
          {isLocked && (
            <button 
              onClick={handleResetLock}
              className="px-2.5 py-1.5 border border-amber-500/30 bg-amber-950/20 hover:bg-amber-900/40 text-amber-400 font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
            >
              Regenerate Connection
            </button>
          )}
          <div className={`px-4 py-2 border rounded-xl font-mono text-xs font-bold tracking-widest transition-all duration-300 flex items-center gap-2 ${
            isLocked 
              ? 'border-red-600 bg-red-950/40 text-red-400 animate-pulse' 
              : 'border-amber-500 bg-amber-950/40 text-amber-500'
          }`}>
            <Clock className={`w-3.5 h-3.5 ${!isLocked ? 'animate-spin' : ''}`} />
            <span>
              {isLocked 
                ? "EXPIRED: TEMPORAL LOCKDOWN ENGAGED" 
                : `60-SECOND TEMPORAL LOCK: 00:${trialTimeLeft < 10 ? '0' + trialTimeLeft : trialTimeLeft}`}
            </span>
          </div>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
        {/* Left column (Slices and input payload) */}
        <div className="lg:col-span-8 flex flex-col space-y-5">
          <div className="space-y-2">
            <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block px-1">
              Select Lead Target Vector
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(Object.keys(targets) as Array<'CORPORATE' | 'GOVERNMENT' | 'INDIVIDUAL'>).map((key) => {
                const targetObj = targets[key];
                const isSelected = activeTarget === key;
                return (
                  <button
                    key={key}
                    disabled={isLocked}
                    onClick={() => setActiveTarget(key)}
                    className={`p-4 border text-left rounded-xl transition-all duration-200 ${
                      isLocked
                        ? 'border-slate-800 bg-slate-950/10 text-slate-700 cursor-not-allowed'
                        : isSelected
                          ? 'border-amber-500 bg-amber-950/30 text-white shadow-[0_0_15px_rgba(245,158,11,0.1)]'
                          : 'border-neutral-900 hover:border-neutral-800 bg-neutral-950/30 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-xs font-mono font-black uppercase text-amber-500">
                      {targetObj.icon}
                      <span>{targetObj.id}</span>
                    </div>
                    <div className="text-xs font-sans font-bold text-slate-200 mt-2">
                      {targetObj.title}
                    </div>
                    <div className="text-[9px] font-mono text-slate-600 mt-1 uppercase">
                      Budget: £{targetObj.depositAmount.toLocaleString()} Base
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Interactive Payload Panel */}
          <div className="space-y-2">
            <label className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block px-1">
              NexoraX Message Payload Customizer
            </label>
            <textarea
              disabled={isLocked || outreachStatus === 'transmitting'}
              rows={4}
              value={customPayload}
              onChange={(e) => setCustomPayload(e.target.value)}
              className="w-full bg-[#050508] border border-neutral-900 focus:border-amber-500/50 rounded-xl p-4 text-xs font-mono text-slate-300 placeholder-neutral-700 focus:outline-none transition-all leading-relaxed"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <button
              onClick={dispatchHermesOutreach}
              disabled={isLocked || outreachStatus === 'transmitting' || !customPayload.trim()}
              className="flex-1 py-3.5 px-6 bg-amber-500 hover:bg-amber-600 disabled:opacity-30 disabled:cursor-not-allowed text-black font-mono text-xs font-black uppercase tracking-widest rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
            >
              <Cpu className="w-4 h-4 fill-current" />
              <span>Broadcast Nex Outreach payload</span>
            </button>
            <a
              href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3.5 px-6 border border-neutral-800 hover:border-amber-500/50 bg-[#060609] hover:bg-zinc-900 text-amber-500 font-mono text-xs font-bold uppercase tracking-widest rounded-xl text-center flex-1"
            >
              Lock Slot (Stripe)
            </a>
            <a
              href="https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134"
              target="_blank"
              rel="noopener noreferrer"
              className="py-3.5 px-6 bg-emerald-500 hover:bg-emerald-400 text-black font-mono text-xs font-black uppercase tracking-widest rounded-xl text-center flex-1 shadow-[0_0_15px_rgba(16,185,129,0.25)]"
            >
              Direct SecurePayment Checkout
            </a>
          </div>

          {/* Progress & Verification triggers */}
          <AnimatePresence>
            {outreachStatus === 'transmitting' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 pt-2"
              >
                <div className="flex justify-between items-center text-[10px] font-mono text-amber-500 font-bold">
                  <span>DISPATCHING OUTREACH ROUTING SIGNALS...</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-neutral-950 border border-neutral-900 h-2 rounded-full overflow-hidden">
                  <div className="bg-amber-500 h-full transition-all duration-150" style={{ width: `${progress}%` }} />
                </div>
              </motion.div>
            )}

            {outreachStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 border border-emerald-500/20 bg-emerald-950/20 rounded-xl flex items-center gap-3.5"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <div className="font-mono text-[10px]">
                  <div className="font-extrabold text-emerald-400 uppercase tracking-widest">TRANSMISSION VECTOR COMPLETED</div>
                  <div className="text-slate-400 mt-1 uppercase leading-snug">
                    Nex Agent matrix has transmitted automated invoice triggers, anchoring target outreach directly to verified target channels.
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right column (Precise system metrics audit splits) */}
        <div className="lg:col-span-4 bg-[#050508] border border-neutral-900 rounded-3xl p-5 flex flex-col justify-between relative min-h-[400px]">
          {/* Lock overlay logic */}
          {isLocked && (
            <div className="absolute inset-0 bg-black/95 rounded-3xl flex flex-col justify-center items-center text-center p-6 z-40 border border-red-500/20">
              <AlertTriangle className="w-10 h-10 text-red-500 mb-3 animate-bounce" />
              <div className="text-xs font-mono font-black text-red-500 uppercase tracking-widest">TEMPORAL STOPPED</div>
              <p className="text-[9px] font-mono text-slate-500 mt-2 mb-4 leading-relaxed uppercase">
                60S trial session limits exceeded. Lock session validation to sustain operational routing.
              </p>
              <button
                onClick={handleResetLock}
                className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-black font-mono text-[9px] font-bold uppercase tracking-widest rounded-lg"
              >
                Regen Session
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-neutral-900">
              <Shield className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] font-mono font-bold tracking-wider text-slate-300 uppercase">
                Nex Financial Allocation Setup
              </span>
            </div>

            <div className="space-y-3 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Gross Budget</span>
                <span className="text-white font-extrabold">£{depositVal.toLocaleString()}</span>
              </div>

              <div className="p-3 bg-amber-950/10 border border-amber-500/10 rounded-lg space-y-1.5 leading-snug">
                <div className="flex justify-between font-bold text-amber-500">
                  <span>1% 3% Community Allocation</span>
                  <span>£{levyVal.toFixed(2)}</span>
                </div>
                <div className="pl-3 text-[10px] space-y-1 text-slate-400">
                  <div className="flex justify-between">
                    <span>Charity 1 (Dunfermline)</span>
                    <span>£{splitVal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Charity 2 (Global Core)</span>
                    <span>£{splitVal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Charity 3 (United Kingdom)</span>
                    <span>£{(splitVal - 0.01).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-500">Remaining Balance</span>
                <span className="text-slate-300">£{afterLevyVal.toLocaleString()}</span>
              </div>

              <div className="flex justify-between font-bold text-amber-500">
                <span>10% Maintenance & Hosting Fee</span>
                <span>£{gavelFeeVal.toFixed(2)}</span>
              </div>

              <div className="pt-2 border-t border-neutral-900 space-y-1.5">
                <div className="flex justify-between font-black text-sm text-emerald-400">
                  <span>Net Settlement</span>
                  <span>£{netSettleVal.toLocaleString()}</span>
                </div>
                <div className="text-[9px] text-[#22c55e]/70 leading-snug">
                  Routing Target: Secure Payment Provider <br />
                  <span className="text-slate-400 font-bold">Sort Code: 04-**-** | Account: ******15</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-neutral-900 mt-4 text-[9px] font-mono text-slate-500 uppercase leading-snug tracking-wider">
            <span>Nex Agent outputs mirror directly to NexoraX Resource Library on Google Drive via CloudSync-02.</span>
          </div>
        </div>
      </div>
    </div>
  );
};
